/* import __COLOCATED_TEMPLATE__ from './error-pie-chart.hbs'; */
import Component from '@glimmer/component';
import { service } from '@ember/service';
// import Constants from '../utils/constants';
import * as d3 from 'd3';
import { action } from '@ember/object';
import { reshuffleDetails } from '../utils/error-utils';
import { getSignalChainComponent } from '../utils/svg-utils';
import { modifier } from 'ember-modifier';

export default class ErrorPieChart extends Component {
  @service intl;
  _centerText;
  _chart;

  insertPie = modifier((element) => {
    if (this.args.pieData === null) return;

    let data = this.args.pieData;
    // set value and percentage of each pie data
    data = this.setPieValuePercentage(data);
    // distribute pie data by percentage for proper display
    data = this.distributePieDataPercentage(data);
    // Reshuffle to move smallest slices between largest ones
    data = reshuffleDetails(data);

    this.drawPie(element, data);
  });

  drawPie(element, data) {
    let blues = d3.quantize((t) => d3.interpolateBlues(t * 0.4 + 0.1), Math.max(data.length, 2));
    const color = d3
      .scaleOrdinal()
      .domain(data.map((d) => d.name))
      .range(blues.reverse());

    const width = element.clientWidth;
    const height = element.clientHeight;

    // chart rotate
    const spin = (-1 * Math.PI) / 2; // 1/4 or circle
    let startAngle = (d) => d.startAngle + spin;
    let endAngle = (d) => d.endAngle + spin;

    const outerRadius = Math.min(width, height) / 2; // - 1 - 20;
    let innerRadius = 0.5 * outerRadius;
    const arc = d3
      .arc()
      .outerRadius(outerRadius)
      .innerRadius(innerRadius)
      .padRadius(500)
      .cornerRadius(5)
      .startAngle(startAngle)
      .endAngle(endAngle);

    let arcLabel = (() => {
      const radius = (Math.min(width, height) / 2) * 0.8;
      return d3.arc().innerRadius(radius).outerRadius(radius).startAngle(startAngle).endAngle(endAngle);
    })();

    // pass percentage in creating pie for display
    let pie = d3
      .pie()
      .sort(null)
      .value((d) => d.percentage);

    const svg = d3.create('svg').attr('viewBox', [-width / 2, -height / 2, width, height]);
    this._centerText = svg
      .append('text')
      .attr('dy', '.35em')
      .attr('text-anchor', 'middle')
      .attr('class', 'chart-center-text');
    this._centerText
      .append('tspan')
      .attr('x', 0)
      .attr('y', 0)
      .style('font-weight', 'normal')
      .text(this.intl.t('messages.error-chart-tooltip'));

    this._chart = (() => {
      const arcs = pie(data);
      const defs = svg.append('defs');
      const chartShadow = defs.append('filter').attr('id', 'chart-shadow').attr('height', '130%');
      chartShadow.append('feGaussianBlur').attr('in', 'SourceAlpha').attr('stdDeviation', 3).attr('result', 'blur');
      chartShadow.append('feOffset').attr('in', 'blur').attr('dx', 2).attr('dy', 2).attr('result', 'offsetBlur');

      const chartFilter = chartShadow.append('feMerge');
      chartFilter.append('feMergeNode').attr('in', 'offsetBlur');
      chartFilter.append('feMergeNode').attr('in', 'SourceGraphic');

      svg
        .append('g')
        .attr('stroke', 'white')
        .selectAll('path')
        .data(arcs)
        .join('path')
        .attr('fill', (d) => color(d.data.name))
        .attr('d', arc)
        .attr('class', (d) => d.data.className)
        .on('click', this.clickHandler)
        .on('mousemove', this.mouseMoveHandler)
        .on('mouseleave', this.mouseOutHandler);
      // .append("title")
      // .text(d => `Click to see more details`); // slices previously showed this: ${d.data.name}: ${d.data.value.toLocaleString()}

      svg
        .append('g')
        .attr('font-family', 'sans-serif')
        .attr('font-size', 14)
        .attr('text-anchor', 'middle')
        .selectAll('text')
        .data(arcs)
        .join('text')
        .attr('transform', (d) => `translate(${arcLabel.centroid(d)})`)
        .on('click', this.clickHandler)
        .on('mousemove', this.mouseMoveHandler)
        .on('mouseleave', this.mouseOutHandler)
        .call(function (text) {
          text
            .append('tspan')
            .attr('y', 0)
            .attr('font-weight', 'normal')
            .attr('font-size', 10)
            .text((d) => d.data.name);
        });
      return svg.node();
    })();

    // Reapply highlights (if it existed)
    if ((!element.firstChild && this.args.errorDetailsExists) || this.args.sliceData) {
      let selectedSlice;
      const chartList = this._chart.children[2].children;
      const labelList = this._chart.children[3].children;
      for (let i = 0; i < labelList.length; i++) {
        if (labelList[i].textContent === this.args.sliceData.name) selectedSlice = chartList[i];
      }

      this._centerText.attr('y', -6).style('font-weight', 'bold').text(this.args.sliceData.name);
      this._centerText
        .append('tspan')
        .attr('x', 0)
        .attr('y', 0)
        .attr('dy', '1.0em')
        .style('font-weight', 'normal')
        .text(Math.round(this.args.sliceData.value).toLocaleString() + 'ppm');
      d3.select(selectedSlice).attr('stroke', '#fff').attr('stroke-width', '2px').style('filter', 'url(#chart-shadow)');
      selectedSlice.classList.add('selected');
    }

    // Remove old chart (if it existed)
    if (element.firstChild) element.firstChild.remove();

    // Add new chart
    element.appendChild(this._chart);
  }

  setPieValuePercentage(data) {
    const totalErrorData = data.map((x) => this.getDetailsSum(x.details)).reduce((a, b) => a + b, 0);
    data.forEach((d) => {
      const value = this.getDetailsSum(d.details);
      d.value = value;
      d.percentage = (value / totalErrorData) * 100;
    });

    return data;
  }

  distributePieDataPercentage(data) {
    // get pie data with less than 3%
    const minPercentageData = data.filter((x) => x.percentage < 3);
    // distribute 3% for pie data from above array
    data.forEach((d) => {
      const pieDataToDistribute = minPercentageData.find((x) => x.name === d.name);
      if (pieDataToDistribute) {
        d.percentage += 1;
      }
    });
    // get 3% from top pie data by percentage
    for (let i = 0; i < minPercentageData.length; i++) {
      const maxData = data.reduce((a, b) => {
        return a.percentage > b.percentage ? a : b;
      });

      data.forEach((d) => {
        if (maxData && maxData.name === d.name) {
          d.percentage -= 1;
        }
      });
    }

    return data;
  }

  getDetailsSum(detailsArr) {
    let value = detailsArr.map((d) => d.value).reduce((a, b) => a + b, 0);
    return value;
  }

  getCurrentTarget(target, text) {
    let currentTarget = target;
    if (currentTarget.tagName === 'text' || currentTarget.tagName === 'tspan') {
      let index = 0;
      const chartList = currentTarget.parentElement.parentElement.children[2].children;
      const labelList = currentTarget.parentElement.parentElement.children[3].children;
      for (let i = 0; i < labelList.length; i++) {
        if (labelList[i].firstChild.textContent === text) index = i;
      }
      currentTarget = chartList[index];
    }
    return currentTarget;
  }

  resetOtherSlices(currentTarget) {
    const chartList = currentTarget.parentElement.parentElement.children[2].children;
    const labelList = currentTarget.parentElement.parentElement.children[3].children;
    for (let i = 0; i < labelList.length; i++) {
      if (this.args.sliceData && labelList[i].textContent !== this.args.sliceData.name) {
        if (chartList[i].attributes.style && chartList[i].attributes.style.value !== 'filter: none;') {
          d3.select(chartList[i]).attr('stroke', 'none').style('filter', 'none');
          chartList[i].classList.remove('selected');
        }
      }
    }
  }

  resetOtherComponents(targetName) {
    let componentHighlights = document.querySelectorAll('.dac-svg .bold');
    componentHighlights.forEach((component) => {
      component.classList.remove('bold');
    });
    if (this.args.sliceData && this.args.sliceData.className === targetName) {
      const component = getSignalChainComponent(targetName);
      if (component) {
        component.classList.add('bold');
      }
    }
  }

  addComponentHighlight(targetName) {
    const component = getSignalChainComponent(targetName);
    if (component) {
      component.classList.add('bold');
    }
  }

  removeComponentHighlight(targetName) {
    const component = getSignalChainComponent(targetName);
    if (this.args.sliceData) {
      if (this.args.sliceData.className !== targetName && component) {
        component.classList.remove('bold');
      }
    } else {
      if (component) {
        component.classList.remove('bold');
      }
    }
  }

  @action clickHandler(e, d) {
    this.args.updateErrorDetails(d.data);
    this.resetOtherSlices(e.currentTarget);
    this.resetOtherComponents(d.data.className);
  }

  @action mouseMoveHandler(e, d) {
    const currentTarget = this.getCurrentTarget(e.currentTarget, d.data.name);
    this.addComponentHighlight(d.data.className);

    this._centerText.attr('y', -6).style('font-weight', 'bold').text(d.data.name);
    this._centerText
      .append('tspan')
      .attr('x', 0)
      .attr('y', 0)
      .attr('dy', '1.0em')
      .style('font-weight', 'normal')
      .text(Math.round(d.data.value).toLocaleString() + 'ppm');
    d3.select(currentTarget).attr('stroke', '#fff').attr('stroke-width', '2px').style('filter', 'url(#chart-shadow)');
    currentTarget.classList.add('selected');
  }

  @action mouseOutHandler(e, d) {
    const currentTarget = this.getCurrentTarget(e.currentTarget, d.data.name);
    this.removeComponentHighlight(d.data.className);

    if (this.args.sliceData) {
      if (this.args.sliceData.name !== d.data.name) {
        this._centerText.select('tspan:last-child').remove();
        this._centerText.attr('y', -6).style('font-weight', 'bold').text(this.args.sliceData.name);
        this._centerText
          .append('tspan')
          .attr('x', 0)
          .attr('y', 0)
          .attr('dy', '1.0em')
          .style('font-weight', 'normal')
          .text(Math.round(this.args.sliceData.value).toLocaleString() + 'ppm');
        if (currentTarget.attributes.style && currentTarget.attributes.style.value !== 'filter: none;') {
          d3.select(currentTarget).attr('stroke', 'none').style('filter', 'none');
          currentTarget.classList.remove('selected');
        }
      }
    } else {
      this._centerText.select('tspan:last-child').remove();
      this._centerText.attr('y', 0).style('font-weight', 'normal').text(this.intl.t('messages.error-chart-tooltip'));
      if (currentTarget.attributes.style && currentTarget.attributes.style.value !== 'filter: none;') {
        d3.select(currentTarget).attr('stroke', 'none').style('filter', 'none');
        currentTarget.classList.remove('selected');
      }
    }
  }
}
